/**
 * Reading what the owner typed, and small formatting helpers the wizard shares. The goal sentence is read
 * the way the prototype reads it (its goalRead): the number, the date after "by", and what is left is what counts.
 */

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const monthIndex = (word: string) => MONTHS.findIndex((m) => m.startsWith(word.toLowerCase().slice(0, 3)));

export const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
export const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** A date as YYYY-MM-DD in local time. */
export const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** "20 Dec 2026". */
export const shortDate = (d: Date) => `${d.getDate()} ${cap(MONTHS[d.getMonth()].slice(0, 3))} ${d.getFullYear()}`;
/** "Wednesday 7 October". */
export const longDay = (d: Date) => `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${cap(MONTHS[d.getMonth()])}`;
/** "7 Oct". */
export const dayMonth = (d: Date) => `${d.getDate()} ${cap(MONTHS[d.getMonth()].slice(0, 3))}`;
/** "30 November". */
export const dayFullMonth = (d: Date) => `${d.getDate()} ${cap(MONTHS[d.getMonth()])}`;

/** The deadline in a goal sentence and where it sits: "by 20 December" or "by December 20", with or without a year. */
function deadline(text: string, today: Date): { date: Date; span: [number, number] } | null {
  const m =
    text.match(/\bby\s+(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)(?:,?\s+(\d{4}))?/i) ??
    text.match(/\bby\s+([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?/i);
  if (!m || m.index === undefined) return null;
  const [day, mon] = /^\d/.test(m[1]) ? [Number(m[1]), m[2]] : [Number(m[2]), m[1]];
  const mi = monthIndex(mon);
  if (mi < 0 || day < 1 || day > 31) return null;
  const now = startOfDay(today);
  let year = m[3] ? Number(m[3]) : now.getFullYear();
  if (!m[3] && new Date(year, mi, day) < now) year++;
  const d = new Date(year, mi, day);
  return d.getMonth() === mi ? { date: d, span: [m.index, m.index + m[0].length] } : null;
}

/** The deadline in a goal sentence: "by 20 December" or "by December 20", with or without a year. */
export const goalDate = (text: string, today = new Date()) => deadline(text, today)?.date ?? null;

export interface GoalReading {
  what: string;
  target: number | null;
  by: Date | null;
  /** Where the number and the deadline sit in the sentence, as [start, end], in order. */
  marks: [number, number][];
}

export function readGoal(text: string, today = new Date()): GoalReading {
  const d = deadline(text, today);
  const n = [...text.matchAll(/\d(?:[\d,]*\d)?/g)].find((x) => !d || (x.index ?? 0) < d.span[0] || (x.index ?? 0) >= d.span[1]);
  const marks: [number, number][] = [];
  if (n) marks.push([n.index ?? 0, (n.index ?? 0) + n[0].length]);
  if (d) marks.push(d.span);
  marks.sort((a, b) => a[0] - b[0]);
  const what = marks
    .toReversed()
    .reduce((t, [a, b]) => t.slice(0, a) + t.slice(b), text)
    .replace(/\s+/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .replace(/^[\s.,;:!?]+|[\s.,;:!?]+$/g, "")
    .replace(/^(get|have|reach)\s+/i, "");
  const target = n ? Number(n[0].replace(/,/g, "")) : NaN;
  return { what: cap(what), target: Number.isFinite(target) && target > 0 ? target : null, by: d?.date ?? null, marks };
}

// Why: "20 real estate agents" says who, not what should happen; the plan would have to guess (register? book a call?).
const ACTION =
  /\b(register(?:s|ed)?|sign(?:s|ed)? ?up|book(?:s|ed)?|buy(?:s)?|bought|purchase[sd]?|order(?:s|ed)?|join(?:s|ed)?|attend(?:s|ed)?|subscribe[sd]?|download(?:s|ed)?|appl(?:y|ies|ied)|enrol(?:l|s|ls|led)?|request(?:s|ed)?|start(?:s|ed)?|try|tries|watch(?:es|ed)?|repl(?:y|ies|ied)|respond(?:s|ed)?|call(?:s|ed)?|schedule[sd]?|pay(?:s)?|paid|install(?:s|ed)?|submit(?:s|ted)?|fill(?:s|ed)?|enquire[sd]?|inquire[sd]?|become|hire[sd]?|refer(?:s|red)?|donate[sd]?|visit(?:s|ed)?|claim(?:s|ed)?|complete[sd]?|finish(?:es|ed)?|sign(?:s|ed)?)\b/i;
const RESULT =
  /\b(registrations?|sign-?ups?|leads?|sales?|bookings?|meetings?|demos?|purchases?|subscribers?|subscriptions?|applications?|enquiries|inquiries|trials?|installs?|clients?|customers?|members?|students?|attendees?|appointments?|deals?|consultations?|referrals?|donations?|tickets?|followers?|users?)\b/i;

/** Whether what is left of the goal says what should happen: something people do, or a result to count. */
export const saysWhat = (what: string) => ACTION.test(what) || RESULT.test(what);

/** Dollars as typed ("8,500" or "25.50") to cents; null when it is not a positive amount. */
export function cents(typed: string): number | null {
  const v = Number(typed.replace(/[$,\s]/g, ""));
  return Number.isFinite(v) && v > 0 ? Math.round(v * 100) : null;
}

/** Cents back to what the owner typed: "8,500", or "25.50" when there are cents. */
export const dollarsTyped = (c: number) => (c % 100 ? (c / 100).toLocaleString("en-US", { minimumFractionDigits: 2 }) : fmt(c / 100));

/**
 * A day inside a plan's words ("7 October 2026", "7 to 20 October", "2026-12-20"). A day with no year is
 * read as the next one on or after a month before `ref`, so a plan written in December can name January.
 */
export function planDay(s: string | undefined, ref = new Date()): Date | null {
  const t = String(s ?? "");
  const iso = t.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const m = t.match(/(\d{1,2})(?:\s*(?:to|-)\s*\d{1,2})?\s+([A-Za-z]{3,})(?:,?\s+(\d{4}))?/);
  if (!m) return null;
  const mi = monthIndex(m[2]);
  if (mi < 0) return null;
  if (m[3]) return new Date(Number(m[3]), mi, Number(m[1]));
  const floor = new Date(ref.getFullYear(), ref.getMonth() - 1, 1);
  const d = new Date(ref.getFullYear(), mi, Number(m[1]));
  if (d < floor) d.setFullYear(d.getFullYear() + 1);
  return d;
}

/** A time from the API in ms. D1 writes "2026-10-01 10:50:47" in UTC with no zone; events carry ISO times. */
export const utcMs = (s: string | null | undefined) => {
  if (!s) return NaN;
  return Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(s) ? `${s.replace(" ", "T")}Z` : s);
};

/** Elapsed time as the prototype shows it: "0:24", "2:17". */
export const clock = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** Words and lists in the prototype's way: "a, b and c". */
export const andList = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
